import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import {
  contractStatusConverter,
  IContactPersonResponse,
  TicketIssueType,
} from '@resident-nx/shared';
import { filter, map } from 'rxjs';
import { TicketCreationService } from '../../../../services';
import { TicketDirectAssignmentComponent } from '../ticket-direct-assignment/ticket-direct-assignment.component';
import { SelectContractWebComponent } from './select-contract/select-contract.component';
import { TicketCategoriesComponent } from './ticket-categories/ticket-categories.component';
import { TicketDetailQuestionsComponent } from './ticket-detail-questions/ticket-detail-questions.component';
import { TicketSummaryComponent } from './ticket-summary/ticket-summary.component';
import { SideSheetFormComponent } from '../../side-sheet-form/side-sheet-form.component';
import { SideSheetContentDirective } from '../../side-sheet-form/directives/side-sheet-content.directive';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SuccessScreenWebComponent } from '../../../atoms/success-screen/success-screen.component';
import { ErrorScreenComponent } from '../../../atoms/error-screen/error-screen.component';
import { ImmomioButtonComponent } from '@immomio/ui/button';
import { FeedbackStore } from '@resident-nx/web/feedback/data-access';
import { FeedbackModalComponent } from '@resident-nx/web/feedback';

@Component({
  standalone: true,
  selector: 'rs-web-ticket-creation-page',
  templateUrl: './ticket-creation-page.component.html',
  styleUrl: './ticket-creation-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    SideSheetFormComponent,
    SelectContractWebComponent,
    SideSheetContentDirective,
    TicketCategoriesComponent,
    TicketDetailQuestionsComponent,
    TicketSummaryComponent,
    ReactiveFormsModule,
    AsyncPipe,
    TicketDirectAssignmentComponent,
    SuccessScreenWebComponent,
    ErrorScreenComponent,
    ImmomioButtonComponent,
  ],
  providers: [TicketCreationService],
})
export class TicketCreationPageWebComponent implements OnInit, OnDestroy {
  #ticketCreationService = inject(TicketCreationService);
  #ngbActiveOffCanvas = inject(NgbActiveOffcanvas);
  #feedbackStore = inject(FeedbackStore);
  #breakpointObserver = inject(BreakpointObserver);
  #destroyRef = inject(DestroyRef);
  public isXsBreakpoint = toSignal(
    this.#breakpointObserver.observe([Breakpoints.XSmall]).pipe(map(result => result.matches))
  );
  public form = this.#ticketCreationService.form;
  public showDetailStep = signal(false);
  public showSummaryStep = signal(false);
  public showSuccessStep = signal(false);
  public showErrorStep = signal(false);
  public contacts = signal<IContactPersonResponse[]>([]);
  public saveTicketActionState = toSignal(this.#ticketCreationService.saveTicketActionState$);
  public detailQuestionsActionState = this.#ticketCreationService.detailQuestionsActionState;
  public contracts$ = this.#ticketCreationService.contracts$.pipe(
    map(contracts =>
      contracts.filter(contract => contractStatusConverter.isContractActive(contract))
    )
  );
  public detailQuestionsFormArray = this.#ticketCreationService.detailQuestionsFormArray;

  @Input() set type(issueType: TicketIssueType) {
    this.#ticketCreationService.type = issueType;
  }

  get detailQuestionsResponse() {
    return this.#ticketCreationService.detailQuestionsResponse;
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  get type() {
    return this.#ticketCreationService.type;
  }

  ngOnInit() {
    this.#ticketCreationService.saveTicketActionState$
      .pipe(
        filter(state => state.done || !!state.error),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(state => {
        if (state.done) {
          this.showSuccessStep.set(state.done);
          this.#ticketCreationService.reFetchTickets();
        } else {
          this.showErrorStep.set(!!state.error);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.showSuccessStep()) {
      this.#feedbackStore.promptIfEligible(FeedbackModalComponent);
    }
  }

  public onCategoriesStepDone(sideSheetFormComponent: SideSheetFormComponent) {
    this.#ticketCreationService
      .onCategoriesStepDone()
      .subscribe(({ contacts, detailQuestions }) => {
        if (contacts.length > 0) {
          this.contacts.set(contacts);
        } else {
          if (detailQuestions.detailRootQuestion) {
            this.showDetailStep.set(true);
          }
          this.showSummaryStep.set(true);
          sideSheetFormComponent.nextTab();
        }
      });
  }

  public onContractChange() {
    this.hideSteps();
    this.#ticketCreationService.onContractChange();
  }

  public onCategoryQuestionChange() {
    this.hideSteps();
    this.#ticketCreationService.clearDetailQuestionsFormArray();
  }

  public onDismiss(event: NgbActiveOffcanvas) {
    event.dismiss();
  }

  public dismiss(): void {
    this.#ngbActiveOffCanvas.dismiss();
  }

  public createTicket() {
    this.#ticketCreationService.createTicket();
  }

  private hideSteps() {
    if (this.showSummaryStep() || this.showDetailStep()) {
      this.showDetailStep.set(false);
      this.showSummaryStep.set(false);
    }
  }

  protected readonly TicketIssueType = TicketIssueType;
}
